import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { FundStatus } from '../types';

interface CreateFundDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (fundData: any) => Promise<void>;
}

interface FundData {
  name: string;
  total_commitments: number;
  vintage: number;
  status: FundStatus;
}

const fundStatus = Object.values(FundStatus);

const fundStatusLabels: { [key: string]: string } = {
  [FundStatus.FUNDRAISING]: 'Fundraising',
  [FundStatus.INVESTING]: 'Investing',
  [FundStatus.HARVESTING_RETURNS]: 'Harvesting Returns',
  [FundStatus.CLOSED]: 'Closed',
};

const CreateFundDialog: React.FC<CreateFundDialogProps> = ({ open, onClose, onSubmit }) => {
  const [fundData, setFundData] = useState<FundData>({
    name: '',
    total_commitments: 0,
    vintage: new Date().getFullYear(),
    status: FundStatus.FUNDRAISING,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFundData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(fundData);
      onClose();
      setFundData({
        name: '',
        total_commitments: 0,
        vintage: new Date().getFullYear(),
        status: FundStatus.FUNDRAISING,
      });
    } catch (error) {
      console.error('Failed to create fund:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          Create New Fund
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Fund Name *"
          type="text"
          fullWidth
          variant="outlined"
          value={fundData.name}
          onChange={handleChange}
          required
        />
        <TextField
          margin="dense"
          name="total_commitments"
          label="Target Size (USD)"
          type="number"
          fullWidth
          variant="outlined"
          value={fundData.total_commitments}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="vintage"
          label="Vintage Year"
          type="number"
          fullWidth
          variant="outlined"
          value={fundData.vintage}
          onChange={handleChange}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={fundData.status}
            onChange={handleChange}
            label="Status"
          >
            {fundStatus.map((status) => (
              <MenuItem key={status} value={status}>
                {fundStatusLabels[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !fundData.name.trim()}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFundDialog;